import React, { useContext, useMemo, useState, useEffect } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Layout from "components/layout/layout";
import { useNavigate } from "react-router-dom";
import { FundSettingsPage } from "constants/pages";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { ContributionReceiptsList } from "../../components/contribution-transaction/contribution-recipits/contribution-receipts-list";
import ContributionTransaction, {
  CashInformation,
  SecuritiesInformation,
  TRANSACTION_STATUS,
  TRANSACTION_TYPE_ENUM,
} from "types/contribution-transaction";
import { PaginatedEntityList } from "types/pagination";
import { numberRange, shortFormat } from "utils/date-format";
import { moneyFormat } from "utils/money-format";
import { MyPendingContributionList } from "./contribution";
import { useGetContributionTransactionsQuery } from "hooks/use-services/contribution-transaction.service";
import { useGetMovementsAvailablePeriodsQuery } from "hooks/use-services/movements.service";
import { FUND_MOVEMENT_TYPE_ENUM } from "types/fund-movements-history";
import useIntersectionObserver from "hooks/use-services/useIntersectionObserver";
import { getContributionTransactionsAPI } from "api/contribution-transaction.api";
import * as XLSX from "xlsx";
const formatContributionDate = (d: Date | string) => {
  const date = typeof d === "string" ? new Date(d) : d;
  return isNaN(date.getTime()) ? "" : shortFormat(date);
};

export const ContributionReceipts = () => {
  const { selectedAccount } = useContext<AuthData>(AuthContext);
  const [limit] = React.useState<any>(18);
  const [year, setYear] = React.useState<number>(new Date().getFullYear());
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: availablePeriods } = useGetMovementsAvailablePeriodsQuery(
    selectedAccount?.fundAccountId || 0,
    [FUND_MOVEMENT_TYPE_ENUM.CONTRIBUTION]
  );

  const availableYears = useMemo(
    () => (availablePeriods ?? []).map((p) => p.year),
    [availablePeriods]
  );

  useEffect(() => {
    if (availableYears.length === 0) {
      return;
    }
    if (!availableYears.includes(year)) {
      setYear(availableYears[0]);
    }
  }, [availableYears, year]);

  const { data: paginatedData, isFetching, isLoading,
    fetchNextPage,
    hasNextPage, isFetchingNextPage } = useGetContributionTransactionsQuery({
      limit: limit,
      fundId: selectedAccount?.fundAccountId || 0,
      status: TRANSACTION_STATUS.ACCEPTED,
      year,
    });
  const pastContributionTransactions = useMemo<ContributionTransaction[]>(
    () =>
      paginatedData ? paginatedData?.pages?.flatMap((item) => item.data) : [],
    [paginatedData]
  );
  const lastElementRef = useIntersectionObserver({
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
  });
  const navigate = useNavigate();

  const handleDownloadExcel = async () => {
    const fundId = selectedAccount?.fundAccountId;
    const fundName = selectedAccount?.fundAccountName ?? "Fund";
    if (!fundId) return;
    setIsDownloading(true);
    try {
      const BATCH = 100;
      let offset: number | undefined = 0;
      const allContributions: ContributionTransaction[] = [];
      while (true) {
        const res: PaginatedEntityList<ContributionTransaction> =
          await getContributionTransactionsAPI({
          fundId,
          limit: BATCH,
          offset,
          status: TRANSACTION_STATUS.ACCEPTED,
          year,
        });
        allContributions.push(...(res.data ?? []));
        if (!res.pagination?.hasNext) break;
        offset = res.pagination.nextOffset;
      }
      const rows = allContributions.map((c) => {
        const isCash = c.type === TRANSACTION_TYPE_ENUM.CASH;
        const isSecurities = c.type === TRANSACTION_TYPE_ENUM.SECURITIES;
        const cashInfo = c.contributionInformation as CashInformation;
        const secInfo = c.contributionInformation as SecuritiesInformation;
        const cashAmount =
          isCash &&
          (cashInfo?.appliedAmount != null || c.contributionReport?.appliedAmount != null)
            ? (cashInfo?.appliedAmount ?? c.contributionReport?.appliedAmount) ?? ""
            : "";
        const quantity = isSecurities ? secInfo?.quantity ?? "" : "";
        const securityTicker = isSecurities ? secInfo?.ticker ?? "" : "";
        const securityName = isSecurities ? secInfo?.securityName ?? "" : "";
        const donorName = c.contributionReport?.name ?? "";
        return {
          Date: formatContributionDate(c.executionDate),
          "Contribution Type": c.type === TRANSACTION_TYPE_ENUM.CASH ? "Cash" : c.type === TRANSACTION_TYPE_ENUM.SECURITIES ? "Securities" : c.type,
          "Cash Amount": typeof cashAmount === "number" ? moneyFormat(cashAmount) : cashAmount,
          "Quantity (of shares / units)": quantity,
          "Security Ticker": securityTicker,
          "Security Name": securityName,
          "Donor Name": donorName,
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).map((key) => ({
        wch: Math.max(key.length, 14),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Contribution Receipts");
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const safeName = fundName.replace(/[/\\:*?"<>|]/g, "_");
      const filename = `${safeName}-Contribution_Receipts_${yyyy}-${mm}-${dd}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } finally {
      setIsDownloading(false);
    }
  };


  return (
    <Layout>
      {() => (
        <>
          <Box sx={{ my: 4, width: "100%" }}>
            <React.Fragment>
              <Container sx={{ my: 3, width: "100%" }}>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                >
                  <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate(FundSettingsPage)}
                  >
                    <Typography
                      variant="bodyCopyBold"
                      sx={{ color: "neutral.main" }}
                    >
                      Fund settings
                    </Typography>
                  </Link>
                  <Typography
                    variant="bodyCopyBold"
                    sx={{ color: "neutral.main" }}
                  >
                    Contribution receipts
                  </Typography>
                </Breadcrumbs>
                <Grid sx={{ mb: 5, mt: 2 }} container spacing={2}>
                  <Grid item xs={12} sm={8} sx={{ mb: 2 }}>
                    <Typography variant="h3">Contribution receipts</Typography>
                  </Grid>
                  <MyPendingContributionList />
                  <Grid item xs={12} sm={12}>
                    <Card sx={{ boxShadow: "none", p: 4 }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{ color: `neutral.main` }}
                            variant="subtitle3"
                          >
                            Past contributions{" "}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={
                                isDownloading ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              onClick={handleDownloadExcel}
                              disabled={
                                isDownloading || !selectedAccount?.fundAccountId
                              }
                            >
                              {isDownloading
                                ? "Downloading…"
                                : "Download activity into Excel"}
                            </Button>
                            <Select
                              id="title-select"
                              size="small"
                              name="title"
                              value={year}
                              onChange={(event) => setYear(+event.target.value)}
                            >
                              {(availableYears.length
                                ? availableYears
                                : numberRange(
                                    new Date().getFullYear(),
                                    new Date().getFullYear() - 2,
                                    -1
                                  )
                              ).map((y) => (
                                <MenuItem key={y} value={y}>
                                  {y === new Date().getFullYear()
                                    ? "Current year"
                                    : y}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </Box>
                        {isLoading && (
                          <Grid
                            item
                            xs={12}
                            sm={10}
                            textAlign={"center"}
                            sx={{ m: 3 }}
                          >
                            {" "}
                            <CircularProgress color="primary" />
                          </Grid>
                        )}
                        {pastContributionTransactions &&
                          pastContributionTransactions.length !== 0 && (
                            <>
                              <ContributionReceiptsList
                                pending={false}
                                contributionTransactions={
                                  pastContributionTransactions
                                }
                              />
                              <Box ref={lastElementRef}>
                                {isFetchingNextPage && (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={10}
                                    textAlign={"center"}
                                    sx={{ m: 3 }}
                                  >
                                    {" "}
                                    <CircularProgress color="primary" />
                                  </Grid>
                                )}
                              </Box>
                            </>
                          )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </React.Fragment>
          </Box>
        </>
      )}
    </Layout>
  );
};
